import { api } from '@/api'
import { endpoints } from '@/api/endpoints'
import {
  GetEstimateConvertOrSellFeePayload,
  EstimateConvertOrSellFeeDTO,
  SendConvertOrSellPayload,
  SendConvertOrSellDTO,
  OnRampPricePerUnitParams,
  OnRampPricePerUnitDTO,
  ExternalWithdrawCalcFeesPayload,
  ExternalWithdrawFeeDTO,
  ExternalWithdrawPayload,
  ExternalWithdrawDTO,
  CheckExternalWalletAddressPayload,
  OnRampCryptoPriceQuotesDTO,
  GetCryptoPriceQuotesAllDTO,
  CreatePurchaseOnRampPayload,
  CreatePurchaseOnRampDTO,
  CreatePurchaseOnRampDTOLink,
  SavingGoalDTO,
  SavingGoalByIDParams,
  CreateSavingGoalPayload,
  UpdateSavingGoalPayload,
  SavingGoalTransferInPayload,
  SavingGoalTransferOutPayload,
  EstimateSavingTransferOutFeePayload,
  EstimateSavingTransferOutFeeDTO,
  GetSavingGoalTransferDetailsParams,
  GoalTransferDTO,
  FetchSavingGoalHistoryParams,
  CashbackInfoParams,
  CashbackInfoDTO,
  OffRampPriceLimitsDTO,
  OffRampPriceEstimatePayload,
  OffRampPriceQuotesPayload,
  OffRampPriceQuotesDTO,
  InitiateOffRampPayload,
  InitiateOffRampDTO,
  OffRampTransactionIdPayload,
} from './transferTypes'

/**
 * Calculate conversion fee
 */
async function calculateConversionFee(
  payload: GetEstimateConvertOrSellFeePayload,
): Promise<EstimateConvertOrSellFeeDTO> {
  const response = await api.post<EstimateConvertOrSellFeeDTO>(
    endpoints.transfer.estimateConvertOrSellFee,
    payload,
  )
  return response.data
}

/**
 * Execute conversion
 */
async function sendConvertOrSell(
  payload: SendConvertOrSellPayload,
): Promise<SendConvertOrSellDTO> {
  const response = await api.post<SendConvertOrSellDTO>(
    endpoints.transfer.convertOrSell,
    payload,
  )
  return response.data
}

/**
 * Get OnRamp price per unit
 */
async function getOnRampPricePerUnit(
  payload: OnRampPricePerUnitParams,
): Promise<OnRampPricePerUnitDTO> {
  const response = await api.get<OnRampPricePerUnitDTO>(
    endpoints.transfer.onRampPricePerUnit(payload.crypto),
    {
      params: {
        fiat: payload.fiat,
      },
    },
  )
  return response.data
}

/**
 * Calculate external withdraw fee
 */
async function calculateExternalWithdrawFee(
  payload: ExternalWithdrawCalcFeesPayload,
): Promise<ExternalWithdrawFeeDTO> {
  try {
    const response = await api.post<ExternalWithdrawFeeDTO>(
      endpoints.transfer.externalWithdrawCalculateFee,
      payload,
    )
    return response.data
  } catch (error) {
    console.error('API: calculateExternalWithdrawFee error:', error)
    throw error
  }
}

/**
 * Create external withdraw
 */
async function createExternalWithdraw(
  payload: ExternalWithdrawPayload,
): Promise<ExternalWithdrawDTO> {
  const response = await api.post<ExternalWithdrawDTO>(
    endpoints.transfer.externalWithdraw,
    payload,
  )
  return response.data
}

/**
 * Check external wallet address
 */
async function checkExternalWalletAddress(
  payload: CheckExternalWalletAddressPayload,
): Promise<void> {
  const response = await api.post<void>(
    endpoints.transfer.checkExternalWalletAddress,
    payload,
  )
  return response.data
}

/**
 * Get crypto price quotes for on-ramp
 */
async function getCryptoPriceQuotesByCurrency(
  payload: OnRampCryptoPriceQuotesDTO,
): Promise<GetCryptoPriceQuotesAllDTO> {
  const response = await api.post<GetCryptoPriceQuotesAllDTO>(
    endpoints.transfer.cryptoPriceQuotes,
    {
      amount: payload.amount,
      fiat_currency: payload.fiatCurrency,
      crypto_currency: payload.cryptoCurrency,
    },
  )
  return response.data
}

/**
 * Create purchase on-ramp
 */
async function createPurchaseOnRamp(
  payload: CreatePurchaseOnRampPayload,
): Promise<CreatePurchaseOnRampDTO> {
  const response = await api.post<CreatePurchaseOnRampDTO>(
    endpoints.transfer.onRamp,
    {
      user_id: payload.userId,
      amount: payload.amount,
      fiat_currency: payload.fiatCurrency,
      crypto_currency: payload.cryptoCurrency,
      payment_method: payload.paymentMethod,
      ...(payload.priceQuotesId && { price_quotes_id: payload.priceQuotesId }),
      // Optional fields (only for send-request flow)
      ...(payload.phoneNumber && { phone_number: payload.phoneNumber }),
      ...(payload.purpose && { purpose: payload.purpose }),
      ...(payload.paymentRequestId && {
        payment_request_id: payload.paymentRequestId,
      }),
      ...(payload.redirectUrl && { redirect_url: payload.redirectUrl }),
    },
  )
  return response.data
}

async function createPurchaseOnRampLink(
  payload: CreatePurchaseOnRampPayload,
): Promise<CreatePurchaseOnRampDTOLink> {
  const response = await api.post<CreatePurchaseOnRampDTOLink>(
    endpoints.transfer.onRampLink,
    {
      user_id: payload.userId,
      amount: payload.amount,
      fiat_currency: payload.fiatCurrency,
      crypto_currency: payload.cryptoCurrency,
      payment_method: payload.paymentMethod,
      ...(payload.priceQuotesId && { price_quotes_id: payload.priceQuotesId }),
      // Optional fields (only for send-request flow)
      ...(payload.phoneNumber && { phone_number: payload.phoneNumber }),
      ...(payload.purpose && { purpose: payload.purpose }),
      ...(payload.paymentRequestId && {
        payment_request_id: payload.paymentRequestId,
      }),
      ...(payload.redirectUrl && { redirect_url: payload.redirectUrl }),
    },
  )
  return response.data
}

/**
 * SAVINGS FUNCTIONS
 */

async function fetchSavingGoals(): Promise<{ saving_goals: SavingGoalDTO[] }> {
  const res = await api.get(endpoints.transfer.goals)

  return res.data
}

async function getSavingGoal(
  params: SavingGoalByIDParams,
): Promise<SavingGoalDTO> {
  const res = await api.get(endpoints.transfer.goalById(params.saving_goal_id))

  return res.data
}

async function createSavingGoal(
  payload: CreateSavingGoalPayload,
): Promise<SavingGoalDTO> {
  const res = await api.post(endpoints.transfer.goals, payload)

  return res.data
}

async function updateSavingGoal({
  saving_goal_id,
  ...payload
}: UpdateSavingGoalPayload): Promise<void> {
  const res = await api.patch(
    endpoints.transfer.goalById(saving_goal_id),
    payload,
  )

  return res.data
}

async function deleteSavingGoal(params: SavingGoalByIDParams): Promise<void> {
  const res = await api.delete(
    endpoints.transfer.goalById(params.saving_goal_id),
  )

  return res.data
}

async function createGoalTransferIn(
  payload: SavingGoalTransferInPayload,
): Promise<void> {
  const res = await api.post(endpoints.transfer.goalTransferIn, payload)

  return res.data
}

async function createGoalTransferOut(
  payload: SavingGoalTransferOutPayload,
): Promise<void> {
  const res = await api.post(endpoints.transfer.goalTransferOut, payload)

  return res.data
}

/**
 * Fetch saving goal history with pagination
 */
async function fetchSavingGoalHistory(
  params: FetchSavingGoalHistoryParams,
): Promise<{ transfers: GoalTransferDTO[]; next_page_id: number | null }> {
  const queryParams = new URLSearchParams()
  if (params.page !== undefined)
    queryParams.append('page', params.page.toString())
  if (params.limit) queryParams.append('limit', params.limit.toString())
  if (params.search) queryParams.append('search', params.search)
  if (params.from_date)
    queryParams.append('from_date', params.from_date.toString())
  if (params.to_date) queryParams.append('to_date', params.to_date.toString())

  const url = `${endpoints.transfer.savingGoalHistory(params.saving_goal_id)}?${queryParams.toString()}`

  const response = await api.get<{
    transfers: GoalTransferDTO[]
    next_page_id: number | null
  }>(url)

  return response.data
}

/**
 * Get saving goal transfer details
 */
async function getSavingGoalTransferDetails(
  params: GetSavingGoalTransferDetailsParams,
): Promise<GoalTransferDTO> {
  const response = await api.get<GoalTransferDTO>(
    endpoints.transfer.savingGoalTransferDetails(
      params.saving_goal_id,
      params.transfer_id,
    ),
  )
  return response.data
}

export const transferApi = {
  calculateConversionFee,
  sendConvertOrSell,
  getOnRampPricePerUnit,
  calculateExternalWithdrawFee,
  createExternalWithdraw,
  checkExternalWalletAddress,
  getCryptoPriceQuotesByCurrency,
  createPurchaseOnRamp,
  createPurchaseOnRampLink,
  // SAVINGS
  fetchSavingGoals,
  getSavingGoal,
  createSavingGoal,
  updateSavingGoal,
  deleteSavingGoal,
  createGoalTransferIn,
  createGoalTransferOut,
  fetchSavingGoalHistory,
  getSavingGoalTransferDetails,
  estimateSavingTransferOutFee,

  // CASHBACK
  getCashbackInfo,

  // OFF-RAMP
  getOffRampPriceLimits,
  getOffRampPriceEstimate,
  getOffRampPriceQuotes,
  initiateOffRamp,
  confirmOffRampPayment,
  cancelOffRamp,
}

/**
 * Estimate saving transfer out fee
 */
async function estimateSavingTransferOutFee(
  payload: EstimateSavingTransferOutFeePayload,
): Promise<EstimateSavingTransferOutFeeDTO> {
  const response = await api.post<EstimateSavingTransferOutFeeDTO>(
    endpoints.transfer.estimateTransferOutFee,
    payload,
  )
  return response.data
}

/**
 * CASHBACK FUNCTIONS
 */

async function getCashbackInfo(
  params: CashbackInfoParams,
): Promise<CashbackInfoDTO> {
  const response = await api.get<CashbackInfoDTO>(
    endpoints.transfer.cashbackInfo,
    {
      params: {
        period: params.period,
        currency: params.currency,
      },
    },
  )
  return response.data
}

/**
 * OFF-RAMP FUNCTIONS
 */

async function getOffRampPriceLimits(): Promise<OffRampPriceLimitsDTO> {
  const response = await api.get<OffRampPriceLimitsDTO>(
    endpoints.transfer.offRampPriceLimits,
  )
  return response.data
}

async function getOffRampPriceEstimate(
  payload: OffRampPriceEstimatePayload,
): Promise<OffRampPriceQuotesDTO> {
  const response = await api.post<OffRampPriceQuotesDTO>(
    endpoints.transfer.offRampPriceEstimate,
    payload,
  )
  return response.data
}

async function getOffRampPriceQuotes(
  payload: OffRampPriceQuotesPayload,
): Promise<OffRampPriceQuotesDTO> {
  const response = await api.post<OffRampPriceQuotesDTO>(
    endpoints.transfer.offRampPriceQuotes,
    payload,
  )
  return response.data
}

async function initiateOffRamp(
  payload: InitiateOffRampPayload,
): Promise<InitiateOffRampDTO> {
  const response = await api.post<InitiateOffRampDTO>(
    endpoints.transfer.initiateOffRamp,
    payload,
  )
  return response.data
}

async function confirmOffRampPayment(
  payload: OffRampTransactionIdPayload,
): Promise<void> {
  const response = await api.post(
    endpoints.transfer.confirmOffRampPayment,
    payload,
  )
  return response.data
}

async function cancelOffRamp(
  payload: OffRampTransactionIdPayload,
): Promise<void> {
  const response = await api.post(
    endpoints.transfer.cancelOffRamp,
    payload,
  )
  return response.data
}
